import Device from 'Faster/model/Device';
import Session from 'Faster/model/Session';
import CloseSessionRequest from 'Faster/request/CloseSessionRequest';
import UpdateSessionRequest from 'Faster/request/UpdateSessionRequest';
import SessionStorage from 'Faster/storage/SessionStorage';
import FasterHttpClient from 'Faster/network/FasterHttpClient';
import CreateSessionRequest from 'Faster/request/CreateSessionRequest';
import DeviceStorage from 'Faster/storage/DeviceStorage';
import FasterConfiguration from 'Faster/FasterConfiguration';

class SessionService {
  private deviceStorage: DeviceStorage;
  private sessionStorage: SessionStorage;
  private fasterClient: FasterHttpClient;
  private configuration: FasterConfiguration;
  private sessionIsExternal: boolean;

  public constructor(
    fasterClient: FasterHttpClient,
    deviceStorage: DeviceStorage,
    sessionStorage: SessionStorage,
    configuration: FasterConfiguration,
    externalSessionId?: string
  ) {
    this.deviceStorage = deviceStorage;
    this.sessionStorage = sessionStorage;
    this.fasterClient = fasterClient;
    this.configuration = configuration;

    this.sessionIsExternal = typeof externalSessionId === 'string';
  }

  public get(): Session {
    return this.sessionStorage.session;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public create(device: Device): Session {
    if (this.sessionIsExternal) {
      return;
    }

    const startingSessionProperties = this.configuration.options.sessionProperties;
    const currentSession = this.sessionStorage.session;

    if (currentSession) {
      this.sessionStorage.session = new Session(undefined, currentSession.userId, currentSession.geoPoint, {
        ...startingSessionProperties,
        ...currentSession.properties,
      });
    } else {
      this.sessionStorage.session = new Session(undefined, undefined, undefined, startingSessionProperties);
    }

    this.checkpoint();

    return this.sessionStorage.session;
  }

  public updateSessionData(session: Session): Promise<Session | void> {
    if (this.sessionIsExternal) {
      console.warn('External Session ID provided, session data manipulation is disabled.');

      return;
    }

    const updateSessionRequest = new UpdateSessionRequest(session, new Date());
    this.sessionStorage.session = session;

    return this.fasterClient
      .execute(updateSessionRequest)
      .then(() => {
        return session;
      })
      .catch((error) => {
        console.warn('Failed to update Session:', error);
      });
  }

  public updateSessionUserId(session: Session): Promise<Session | void> {
    const updateSessionRequest = new UpdateSessionRequest(session, new Date());
    this.sessionStorage.session = session;

    return this.fasterClient
      .execute(updateSessionRequest)
      .then(() => {
        return session;
      })
      .catch((error) => {
        console.warn('Failed to update Session:', error);
      });
  }

  public refresh(device: Device): void {
    if (this.sessionIsExternal) {
      return;
    }

    const sessionExpired = this.isSessionExpired();

    if (sessionExpired) {
      this.closeSession();
      this.create(device);
      this.persistSession();
    } else {
      this.checkpoint();
    }
  }

  public markAsRemotelyPersisted() {
    this.sessionStorage.session = this.sessionStorage.session.persisted();
  }

  private persistSession(): void {
    if (this.sessionStorage.session.remotelyPersisted) {
      return;
    }

    const persistedSession = this.sessionStorage.session.persisted();
    this.fasterClient
      .execute(new CreateSessionRequest(this.deviceStorage.getDevice(), persistedSession))
      .then(() => {
        const isAnotherSession = this.sessionStorage.session.id !== persistedSession.id;
        if (isAnotherSession) {
          return;
        }

        this.sessionStorage.session = this.sessionStorage.session.persisted();
      })
      .catch(() => {});
  }

  private checkpoint(): void {
    this.sessionStorage.checkpoint = new Date();
  }

  private closeSession(): void {
    const session = this.sessionStorage.session;
    if (session == null || session.closed) {
      return;
    }

    const sessionIsRemotelyPersisted = true === session.remotelyPersisted;
    if (!sessionIsRemotelyPersisted) {
      return;
    }

    this.fasterClient
      .execute(new CloseSessionRequest(session, new Date()))
      .then(() => {
        const isAnotherSession = this.sessionStorage.session.id !== session.id;
        if (isAnotherSession) {
          return;
        }

        this.sessionStorage.session = this.sessionStorage.session.close();
      })
      .catch(() => {});
  }

  private isSessionExpired(): boolean {
    const checkpoint = this.sessionStorage.checkpoint;
    if (null === checkpoint) {
      return true;
    }

    const currentTime = new Date().getTime() / 1000;
    const checkpointTime = checkpoint.getTime() / 1000;
    const { sessionTtl } = this.configuration.options;

    return currentTime - checkpointTime > sessionTtl;
  }
}

export default SessionService;
